import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Dropdown, ErrorBox, initials } from '../components';
import { useMeta } from '../meta';
import { t, tp } from '../i18n';

interface TeamCard {
  id: number;
  name: string;
  access_level: number;
  project_count: number;
  member_count: number;
}

const TILE_COLORS = ['#C239B3', '#0F6CBD', '#DA3B01', '#8764B8', '#C50F1F', '#038387', '#107C10', '#7A7574', '#CA5010', '#4F6BED', '#B4009E', '#00838F'];
function tileColor(name: string) {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) | 0;
  return TILE_COLORS[Math.abs(h) % TILE_COLORS.length];
}

export function TeamTile({ name, size = 72 }: { name: string; size?: number }) {
  return (
    <span className="team-tile" style={{ width: size, height: size, background: tileColor(name), fontSize: size * 0.34 }} aria-hidden>
      {initials(name)}
    </span>
  );
}

/** Makes `teamId` the current team, then reloads all data and opens `to`. */
export function useSwitchTeam() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (teamId: number, to = '/') => {
    await api.post('/api/teams/switch', { team_id: teamId });
    try {
      localStorage.removeItem('bt.project');
    } catch {
      /* storage unavailable */
    }
    navigate(to);
    await qc.resetQueries();
  };
}

const ICON: Record<string, ReactNode> = {
  issues: <path d="M4 5h16v14H4zM4 9h16M8 13h8M8 16h5" />,
  board: <path d="M4 4h5v16H4zM10 4h5v10h-5zM16 4h4v7h-4z" />,
  members: <path d="M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M16 4.5a3.2 3.2 0 010 6.2M18 14c2.2.6 3.5 2.6 3.5 5" />,
  dots: <path d="M5 12h.01M12 12h.01M19 12h.01" />,
  plus: <path d="M12 5v14M5 12h14" />,
};
function Icon({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={name === 'dots' ? 3 : 1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {ICON[name]}
    </svg>
  );
}

export function TeamsPage() {
  const { meta, label } = useMeta();
  const qc = useQueryClient();
  const switchTeam = useSwitchTeam();
  const [filter, setFilter] = useState('');
  const { data, error, isLoading } = useQuery({ queryKey: ['teams'], queryFn: () => api.get<{ current_team_id: number | null; teams: TeamCard[] }>('/api/teams') });
  const leave = useMutation({
    mutationFn: (id: number) => api.post(`/api/teams/${id}/leave`),
    onSuccess: () => qc.resetQueries(),
  });

  const teams = (data?.teams ?? []).filter((tm) => !filter || tm.name.toLowerCase().includes(filter.toLowerCase()));
  const current = data?.current_team_id ?? meta.team?.id ?? null;

  const open = (tm: TeamCard, to = '/') => void switchTeam(tm.id, to);

  return (
    <div className="teams-page">
      <div className="page-head">
        <h1>{t('Teams')}</h1>
        {(data?.teams.length ?? 0) > 8 && <input className="short" placeholder={t('Search teams')} value={filter} onChange={(e) => setFilter(e.target.value)} />}
      </div>
      <ErrorBox error={error || leave.error} />
      {isLoading && <p className="muted">{t('Loading…')}</p>}
      {!isLoading && data?.teams.length === 0 && (
        <div className="banner info">{t("You're not in a team yet. Create one below, or open the invitation link someone sent you.")}</div>
      )}
      <div className="team-grid">
        {teams.map((tm) => {
          const admin = tm.access_level >= 90;
          return (
            <div
              key={tm.id}
              className={`team-card ${tm.id === current ? 'current' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => open(tm)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') open(tm);
              }}
              title={t('Open {team}', { team: tm.name })}
            >
              <div className="team-card-top">
                <TeamTile name={tm.name} />
                <div className="team-card-name">
                  <strong>{tm.name}</strong>
                  <span className="subtle small">
                    {label('access_level', tm.access_level)}
                  </span>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    align="right"
                    trigger={() => (
                      <button className="icon-btn" title={t('More options')} aria-label={t('More options')}>
                        <Icon name="dots" />
                      </button>
                    )}
                  >
                    {(close) => (
                      <div className="user-menu">
                        <button className="menu-item" onClick={() => { close(); open(tm); }}>
                          {t('Open team')}
                        </button>
                        {admin && (
                          <button className="menu-item" onClick={() => { close(); open(tm, '/manage/team'); }}>
                            {t('Members & invitations')}
                          </button>
                        )}
                        <div className="menu-sep" />
                        <button
                          className="menu-item danger"
                          onClick={() => {
                            close();
                            if (confirm(t('Leave the team "{team}"? You lose access to its projects.', { team: tm.name }))) leave.mutate(tm.id);
                          }}
                        >
                          {t('Leave team')}
                        </button>
                      </div>
                    )}
                  </Dropdown>
                </div>
              </div>
              <div className="team-card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="icon-btn" title={`${t('Issues')} · ${tp(tm.project_count, '{n} project', '{n} projects')}`} onClick={() => open(tm, '/issues')}>
                  <Icon name="issues" />
                </button>
                <button className="icon-btn" title={t('Board')} onClick={() => open(tm, '/board')}>
                  <Icon name="board" />
                </button>
                <button
                  className="icon-btn"
                  title={tp(tm.member_count, '{n} member', '{n} members')}
                  onClick={() => open(tm, admin ? '/manage/team' : '/')}
                >
                  <Icon name="members" />
                  <span className="icon-count">{tm.member_count}</span>
                </button>
              </div>
            </div>
          );
        })}
        {!filter && <CreateTeamCard />}
      </div>
    </div>
  );
}

function CreateTeamCard() {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const switchTeam = useSwitchTeam();
  const create = useMutation({
    mutationFn: () => api.post<{ id: number }>('/api/teams', { name: name.trim() }),
    onSuccess: (r) => switchTeam(r.id, '/manage/projects'),
  });

  if (!editing) {
    return (
      <div
        className="team-card create"
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        <div className="team-card-top">
          <span className="team-tile add" aria-hidden>
            <Icon name="plus" size={30} />
          </span>
          <div className="team-card-name">
            <strong>{t('Create a team')}</strong>
          </div>
        </div>
      </div>
    );
  }
  return (
    <form
      className="team-card create editing"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (name.trim()) create.mutate();
      }}
    >
      <label className="block">
        {t('Team name')}
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Mobile squad')} maxLength={80} />
      </label>
      <ErrorBox error={create.error} />
      <div className="buttons">
        <button className="primary" disabled={create.isPending || !name.trim()}>
          {t('Create team')}
        </button>
        <button type="button" onClick={() => setEditing(false)}>
          {t('Cancel')}
        </button>
      </div>
    </form>
  );
}
